import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiUsers,
  FiBarChart2,
  FiChevronDown,
  FiRefreshCw,
  FiX,
  FiTrendingUp,
  FiTrendingDown,
  FiMinus,
  FiSearch,
} from "react-icons/fi";
import {
  getDashboardSummary,
  getDepartmentEmployees,
  getEmployeeHistory,
  getEmployeeCategoryBreakdown,
} from "../api/client";
import { useAuth } from "../context/AuthContext";
import Pagination from "../components/Pagination";
import OrgTree from "../components/OrgTree";

const DashboardOrganization = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [expandedDept, setExpandedDept] = useState(null);
  const [deptEmployees, setDeptEmployees] = useState({});
  const [deptLoading, setDeptLoading] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeHistory, setEmployeeHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const mapEmployee = (e) => ({
    id: e.id,
    name: e.name,
    designation: e.designation,
    hierarchy: e.hierarchy,
    email: e.email,
    businessUnit: e.business_unit,
    score: e.latest_score_percentage,
    belt: e.belt,
    beltColor: e.belt_color,
    tier: e.tier,
    totalAttempts: e.total_attempts,
    lastAssessedAt: e.last_assessed_at,
    userId: e.user_id,
  });

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const summaryRes = await getDashboardSummary();
        const s = summaryRes.data;
        const mappedSummary = {
          totalEmployees: s.total_employees || 0,
          assessed: s.total_assessed || 0,
          notAssessed: s.total_not_assessed || 0,
          averageScore: s.overall_avg_score,
          greenCount: s.green_belt_count || 0,
          yellowCount: s.yellow_belt_count || 0,
          redCount: s.red_belt_count || 0,
          departments: (s.departments || []).map(d => ({
            businessUnit: d.business_unit,
            totalEmployees: d.total_employees,
            assessedCount: d.assessed_count,
            notAssessedCount: d.not_assessed_count,
            averageScore: d.avg_score,
            greenCount: d.green_belt_count || 0,
            yellowCount: d.yellow_belt_count || 0,
            redCount: d.red_belt_count || 0,
          })),
        };
        setSummary(mappedSummary);
        setDepartments(mappedSummary.departments);
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch dashboard data:", err);
        setError(err.message);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleToggleDept = async (unit) => {
    if (expandedDept === unit) {
      setExpandedDept(null);
      return;
    }

    setExpandedDept(unit);

    // Fetch employees if not already loaded
    if (!deptEmployees[unit]) {
      setDeptLoading(unit);
      try {
        const res = await getDepartmentEmployees(unit);
        // /api/dashboard/department/{bu} returns a flat list[EmployeeWithScore]
        // (response_model=list[EmployeeWithScore]), NOT an object with an
        // .employees key. Old code read res.data.employees which was always
        // undefined → empty table. Take the array directly.
        const list = Array.isArray(res.data)
          ? res.data
          : res.data?.employees || [];
        const mapped = list.map(mapEmployee);
        setDeptEmployees(prev => ({
          ...prev,
          [unit]: mapped,
        }));
      } catch (err) {
        console.error(`Failed to fetch employees for ${unit}:`, err);
      } finally {
        setDeptLoading(null);
      }
    }
  };

  const handleEmployeeClick = async (emp) => {
    setSelectedEmployee(emp);
    setHistoryLoading(true);
    try {
      const res = await getEmployeeHistory(emp.id);
      const h = res.data;
      const mappedHistory = {
        attempts: (h.attempts || []).map((att, idx) => ({
          sessionId: att.session_id,
          id: att.id,
          attemptNumber: h.attempts.length - idx,
          score: att.score,
          beltColor: att.belt_color,
          completedAt: att.completed_at,
        })),
      };
      setEmployeeHistory(mappedHistory);
    } catch (err) {
      console.error("Failed to fetch employee history:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "var(--bg-primary)",
          color: "var(--text-secondary)",
        }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <FiRefreshCw size={32} />
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: "32px",
          backgroundColor: "var(--bg-primary)",
          color: "var(--text-primary)",
          minHeight: "100vh",
        }}
      >
        <div
          style={{
            backgroundColor: "#fee2e2",
            border: "1px solid #fecaca",
            borderRadius: "var(--radius-md)",
            padding: "16px",
            color: "#991b1b",
          }}
        >
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "32px", backgroundColor: "var(--bg-primary)", minHeight: "100vh" }}>
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ marginBottom: "32px" }}
      >
        <h1
          style={{
            margin: "0 0 8px 0",
            fontSize: "32px",
            fontWeight: "700",
            color: "var(--text-primary)",
          }}
        >
          Organization
        </h1>
        <p
          style={{
            margin: "0",
            fontSize: "16px",
            color: "var(--text-secondary)",
          }}
        >
          Organization structure and employee breakdown
        </p>
      </motion.div>

      {/* OrgTree Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          backgroundColor: "var(--bg-card)",
          borderRadius: "var(--radius-md)",
          border: "1px solid var(--border-subtle)",
          overflow: "hidden",
          marginBottom: "32px",
        }}
      >
        <div style={{ padding: "24px", borderBottom: "1px solid var(--border-subtle)" }}>
          <h2
            style={{
              margin: "0",
              fontSize: "20px",
              fontWeight: "700",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: "var(--text-primary)",
            }}
          >
            <FiUsers size={20} style={{ color: "var(--color-highlight)" }} />
            Organizational Hierarchy
          </h2>
        </div>
        <div style={{ padding: "24px" }}>
          <OrgTree />
        </div>
      </motion.div>

      {/* Organization Table */}
      <DepartmentTable
        departments={departments}
        expandedDept={expandedDept}
        onToggle={handleToggleDept}
        deptEmployees={deptEmployees}
        deptLoading={deptLoading}
        onEmployeeClick={handleEmployeeClick}
      />

      {/* History Modal */}
      <AnimatePresence>
        {selectedEmployee && (
          <HistoryModal
            employee={selectedEmployee}
            history={employeeHistory}
            loading={historyLoading}
            onClose={() => {
              setSelectedEmployee(null);
              setEmployeeHistory(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Department Table Component
const DepartmentTable = ({
  departments,
  expandedDept,
  onToggle,
  deptEmployees,
  deptLoading,
  onEmployeeClick,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        backgroundColor: "var(--bg-card)",
        borderRadius: "var(--radius-md)",
        border: "1px solid var(--border-subtle)",
        overflow: "hidden",
        marginBottom: "32px",
      }}
    >
      <div style={{ padding: "24px", borderBottom: "1px solid var(--border-subtle)" }}>
        <h2
          style={{
            margin: "0",
            fontSize: "20px",
            fontWeight: "700",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "var(--text-primary)",
          }}
        >
          <FiUsers size={20} style={{ color: "var(--color-highlight)" }} />
          Organizations
        </h2>
      </div>

      <div>
        {departments.map((dept, idx) => (
          <DepartmentRow
            key={dept.businessUnit}
            dept={dept}
            isExpanded={expandedDept === dept.businessUnit}
            onToggle={() => onToggle(dept.businessUnit)}
            employees={deptEmployees[dept.businessUnit]}
            isLoading={deptLoading === dept.businessUnit}
            onEmployeeClick={onEmployeeClick}
            isLast={idx === departments.length - 1}
          />
        ))}
      </div>
    </motion.div>
  );
};

// Department Row Component
const DepartmentRow = ({
  dept,
  isExpanded,
  onToggle,
  employees,
  isLoading,
  onEmployeeClick,
  isLast,
}) => {
  return (
    <div>
      <motion.button
        onClick={onToggle}
        style={{
          width: "100%",
          padding: "16px 24px",
          backgroundColor: "transparent",
          border: "none",
          borderBottom: isLast ? "none" : "1px solid var(--border-subtle)",
          color: "var(--text-primary)",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          transition: "background-color 0.2s",
        }}
        whileHover={{ backgroundColor: "var(--bg-card)" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flex: 1,
          }}
        >
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <FiChevronDown size={18} style={{ color: "var(--color-highlight)" }} />
          </motion.div>
          <div style={{ textAlign: "left" }}>
            <h3 style={{ margin: "0", fontSize: "16px", fontWeight: "700" }}>
              {dept.businessUnit}
            </h3>
            <p
              style={{
                margin: "2px 0 0 0",
                fontSize: "12px",
                color: "var(--text-secondary)",
              }}
            >
              {dept.totalEmployees} employees
            </p>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "24px",
            fontSize: "14px",
          }}
        >
          <div style={{ textAlign: "right", minWidth: "60px" }}>
            <p style={{ margin: "0", color: "var(--text-secondary)", fontSize: "12px" }}>
              Assessed
            </p>
            <p
              style={{
                margin: "2px 0 0 0",
                fontWeight: "700",
                color: "var(--text-primary)",
              }}
            >
              {dept.assessedCount}
            </p>
          </div>
          <div style={{ textAlign: "right", minWidth: "80px" }}>
            <p style={{ margin: "0", color: "var(--text-secondary)", fontSize: "12px" }}>
              Avg Score
            </p>
            <p
              style={{
                margin: "2px 0 0 0",
                fontWeight: "700",
                color: "var(--color-highlight)",
              }}
            >
              {dept.averageScore != null ? `${dept.averageScore.toFixed(1)}%` : "N/A"}
            </p>
          </div>
          <div style={{ display: "flex", gap: "4px", minWidth: "120px" }}>
            <BeltBadge count={dept.greenCount || 0} color="#10b981" />
            <BeltBadge count={dept.yellowCount || 0} color="#eab308" />
            <BeltBadge count={dept.redCount || 0} color="#ef4444" />
          </div>
        </div>
      </motion.button>

      {/* Employee List */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              backgroundColor: "var(--bg-card)",
              borderBottom: "1px solid var(--border-subtle)",
            }}
          >
            <EmployeeList
              employees={employees}
              onEmployeeClick={onEmployeeClick}
              isLoading={isLoading}
              maxShow={50}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Belt Badge Component
const BeltBadge = ({ count, color }) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "4px",
        padding: "4px 8px",
        backgroundColor: `${color}20`,
        borderRadius: "4px",
        border: `1px solid ${color}40`,
        minWidth: "36px",
      }}
    >
      <div
        style={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          backgroundColor: color,
        }}
      />
      <span style={{ fontSize: "12px", fontWeight: "700", color }}>
        {count}
      </span>
    </div>
  );
};

// Employee List Component — searchable, filterable, paginated
const EmployeeList = ({ employees, onEmployeeClick, isLoading, maxShow = 50 }) => {
  const [search, setSearch] = useState("");
  const [filterBelt, setFilterBelt] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const PAGE_SZ = 15;

  if (isLoading || !employees) {
    return (
      <div style={{ padding: "24px", textAlign: "center", color: "var(--text-secondary)" }}>
        <FiRefreshCw style={{ animation: "spin 1s linear infinite", marginRight: "8px" }} />
        Loading employees...
      </div>
    );
  }

  const filtered = employees.filter((emp) => {
    const q = search.toLowerCase();
    const matchesSearch = !q || emp.name?.toLowerCase().includes(q) || emp.designation?.toLowerCase().includes(q) || emp.email?.toLowerCase().includes(q) || emp.businessUnit?.toLowerCase().includes(q);
    const matchesBelt = filterBelt === "all" || (filterBelt === "none" ? !emp.belt : emp.belt?.toLowerCase() === filterBelt);
    const matchesStatus = filterStatus === "all" || (filterStatus === "assessed" ? emp.score != null : emp.score == null);
    return matchesSearch && matchesBelt && matchesStatus;
  });

  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0;
    if (sortBy === "name") cmp = (a.name || "").localeCompare(b.name || "");
    else if (sortBy === "score") cmp = (a.score ?? -1) - (b.score ?? -1);
    else if (sortBy === "date") cmp = new Date(a.lastAssessedAt || 0) - new Date(b.lastAssessedAt || 0);
    return sortDir === "desc" ? -cmp : cmp;
  });

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  const thStyle = {
    padding: "8px 10px", fontSize: "11px", fontWeight: "700", color: "var(--text-secondary)",
    textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "left", cursor: "pointer",
    userSelect: "none", borderBottom: "1px solid var(--border-subtle)", whiteSpace: "nowrap",
  };
  const tdStyle = { padding: "8px 10px", fontSize: "13px", borderBottom: "1px solid var(--border-subtle)" };
  const sortIcon = (col) => sortBy === col ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  return (
    <div style={{ padding: "12px 16px" }}>
      {/* Search + Filters */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "10px", flexWrap: "wrap", alignItems: "center" }}>
        <div style={{
          display: "flex", alignItems: "center", gap: "8px", flex: 1, minWidth: "180px",
          padding: "6px 10px", backgroundColor: "var(--bg-primary)", border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-md)",
        }}>
          <FiSearch size={14} style={{ color: "var(--text-secondary)", flexShrink: 0 }} />
          <input
            type="text" placeholder="Search name, designation, email..."
            value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ flex: 1, border: "none", background: "none", color: "var(--text-primary)", fontSize: "13px", outline: "none" }}
          />
        </div>
        <select value={filterBelt} onChange={(e) => { setFilterBelt(e.target.value); setPage(1); }} style={{
          padding: "6px 8px", fontSize: "12px", backgroundColor: "var(--bg-primary)", color: "var(--text-primary)",
          border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", cursor: "pointer",
        }}>
          <option value="all">All Belts</option>
          <option value="green">Green</option>
          <option value="yellow">Yellow</option>
          <option value="red">Red</option>
          <option value="none">No Belt</option>
        </select>
        <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }} style={{
          padding: "6px 8px", fontSize: "12px", backgroundColor: "var(--bg-primary)", color: "var(--text-primary)",
          border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", cursor: "pointer",
        }}>
          <option value="all">All Status</option>
          <option value="assessed">Assessed</option>
          <option value="not_assessed">Not Assessed</option>
        </select>
        <span style={{ fontSize: "12px", color: "var(--text-secondary)", padding: "4px 0" }}>
          {filtered.length} of {employees.length}
        </span>
      </div>

      {/* Table */}
      <div style={{ border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "700px" }}>
          <thead style={{ backgroundColor: "var(--bg-primary)" }}>
            <tr>
              <th style={thStyle} onClick={() => handleSort("name")}>Name{sortIcon("name")}</th>
              <th style={thStyle}>Designation</th>
              <th style={thStyle}>Business Unit</th>
              <th style={thStyle} onClick={() => handleSort("score")}>Score{sortIcon("score")}</th>
              <th style={thStyle}>Belt</th>
              <th style={thStyle}>Attempts</th>
              <th style={thStyle} onClick={() => handleSort("date")}>Last Assessed{sortIcon("date")}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ padding: "24px", textAlign: "center", color: "var(--text-secondary)", fontSize: "13px" }}>
                  No employees match your filters
                </td>
              </tr>
            ) : sorted.slice((page - 1) * PAGE_SZ, page * PAGE_SZ).map((emp) => (
              <tr
                key={emp.id}
                onClick={() => onEmployeeClick(emp)}
                style={{ cursor: "pointer", transition: "background-color 0.15s" }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "var(--bg-card)"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
              >
                <td style={tdStyle}>
                  <div style={{ fontWeight: "700" }}>{emp.name}</div>
                  {emp.email && (
                    <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "1px" }}>
                      {emp.email}
                    </div>
                  )}
                </td>
                <td style={{ ...tdStyle, color: "var(--text-secondary)", fontSize: "12px", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {emp.designation || "—"}
                </td>
                <td style={{ ...tdStyle, color: "var(--text-secondary)", fontSize: "12px", whiteSpace: "nowrap" }}>
                  {emp.businessUnit || "—"}
                </td>
                <td style={{ ...tdStyle, fontWeight: "700", color: emp.score != null ? "var(--text-primary)" : "var(--text-secondary)" }}>
                  {emp.score != null ? `${emp.score}%` : "—"}
                </td>
                <td style={tdStyle}>
                  <div style={{
                    width: "10px", height: "10px", borderRadius: "50%",
                    backgroundColor: emp.beltColor ? getBeltColor(emp.beltColor) : "#6b7280",
                    display: "inline-block",
                  }} />
                </td>
                <td style={{ ...tdStyle, fontSize: "12px", color: "var(--text-secondary)", textAlign: "center" }}>
                  {emp.totalAttempts || 0}
                </td>
                <td style={{ ...tdStyle, fontSize: "12px", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                  {emp.lastAssessedAt ? new Date(emp.lastAssessedAt).toLocaleDateString() : "Not assessed"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          currentPage={page}
          totalPages={Math.ceil(sorted.length / PAGE_SZ)}
          onPageChange={setPage}
          totalItems={sorted.length}
          pageSize={PAGE_SZ}
        />
      </div>
    </div>
  );
};

// History Modal Component
const EmployeeCategoryBreakdown = ({ employeeId }) => {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | error

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    getEmployeeCategoryBreakdown(employeeId)
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setState("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setState("error");
      });
    return () => { cancelled = true; };
  }, [employeeId]);

  if (state === "loading") {
    return <p style={{ fontSize: "13px", color: "var(--text-secondary)", margin: 0 }}>Loading category breakdown…</p>;
  }
  if (state === "error" || !data?.categories?.length) {
    return null;
  }

  const cats = [...data.categories].sort((a, b) => b.score_percentage - a.score_percentage);

  return (
    <div style={{
      marginBottom: "16px",
      padding: "16px",
      backgroundColor: "var(--bg-card)",
      border: "1px solid var(--border-subtle)",
      borderRadius: "var(--radius-md)",
    }}>
      <div style={{
        fontSize: "13px",
        fontWeight: 700,
        color: "var(--text-primary)",
        marginBottom: "10px",
        display: "flex",
        alignItems: "center",
        gap: "6px",
      }}>
        <FiBarChart2 size={14} /> Best attempt — score by category
      </div>
      <div style={{ display: "grid", gap: "8px" }}>
        {cats.map((c) => {
          const pct = c.score_percentage || 0;
          const color = pct >= 70 ? "#10b981" : pct >= 40 ? "#eab308" : "#ef4444";
          return (
            <div key={c.category}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", marginBottom: "3px" }}>
                <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{c.category}</span>
                <span style={{ color, fontWeight: 700 }}>{c.correct}/{c.total} · {pct.toFixed(1)}%</span>
              </div>
              <div style={{ height: "5px", borderRadius: "3px", background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${pct}%`, background: color }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const HistoryModal = ({ employee, history, loading, onClose }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "24px",
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: "var(--bg-primary)",
          borderRadius: "var(--radius-md)",
          border: "1px solid var(--border-subtle)",
          maxWidth: "600px",
          width: "100%",
          maxHeight: "80vh",
          overflow: "auto",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.3)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "24px",
            borderBottom: "1px solid var(--border-subtle)",
            position: "sticky",
            top: 0,
            backgroundColor: "var(--bg-primary)",
          }}
        >
          <div>
            <h2 style={{ margin: "0 0 4px 0", fontSize: "20px", fontWeight: "700" }}>
              {employee.name}
            </h2>
            <p
              style={{
                margin: "0",
                fontSize: "13px",
                color: "var(--text-secondary)",
              }}
            >
              {employee.designation} • Assessment History
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "var(--text-secondary)",
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "var(--text-primary)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "var(--text-secondary)";
            }}
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: "24px" }}>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
                color: "var(--text-secondary)",
              }}
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              >
                <FiRefreshCw size={24} />
              </motion.div>
            </div>
          ) : history?.attempts?.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <EmployeeCategoryBreakdown employeeId={employee.id} />
              {history.attempts.map((attempt, idx) => {
                const prevScore =
                  idx < history.attempts.length - 1 ? history.attempts[idx + 1].score : null;
                const trend = prevScore
                  ? attempt.score > prevScore
                    ? "up"
                    : attempt.score < prevScore
                      ? "down"
                      : "flat"
                  : null;

                return (
                  <motion.div
                    key={attempt.sessionId || attempt.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    style={{
                      backgroundColor: "var(--bg-card)",
                      border: "1px solid var(--border-subtle)",
                      borderRadius: "var(--radius-md)",
                      padding: "16px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <p
                        style={{
                          margin: "0 0 4px 0",
                          fontSize: "14px",
                          fontWeight: "700",
                        }}
                      >
                        Attempt {attempt.attemptNumber}
                      </p>
                      <p
                        style={{
                          margin: "0",
                          fontSize: "12px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        {new Date(attempt.completedAt).toLocaleDateString()}{" "}
                        {new Date(attempt.completedAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                      }}
                    >
                      <div
                        style={{
                          textAlign: "right",
                        }}
                      >
                        <p
                          style={{
                            margin: "0",
                            fontSize: "18px",
                            fontWeight: "700",
                            color: "var(--color-highlight)",
                          }}
                        >
                          {attempt.score}%
                        </p>
                        {trend && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              justifyContent: "flex-end",
                              marginTop: "4px",
                              fontSize: "11px",
                              color:
                                trend === "up"
                                  ? "#10b981"
                                  : trend === "down"
                                    ? "#ef4444"
                                    : "var(--text-secondary)",
                            }}
                          >
                            {trend === "up" ? (
                              <FiTrendingUp size={12} />
                            ) : trend === "down" ? (
                              <FiTrendingDown size={12} />
                            ) : (
                              <FiMinus size={12} />
                            )}
                            {prevScore !== null && `vs ${prevScore}%`}
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          backgroundColor: getBeltColor(attempt.beltColor),
                        }}
                      />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "32px 16px",
                color: "var(--text-secondary)",
              }}
            >
              <FiBarChart2 size={32} style={{ opacity: 0.3, marginBottom: "12px" }} />
              <p>No assessment history available</p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Helper function to get belt color
const getBeltColor = (belt) => {
  const belts = {
    green: "#10b981",
    yellow: "#eab308",
    red: "#ef4444",
    notAssessed: "#6b7280",
  };
  return belts[belt] || "#6b7280";
};

export default DashboardOrganization;
